import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, Minus, Plus, Star, Truck, Leaf, Heart, ShieldAlert, ShoppingBag, Sparkles } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppFab from "@/components/WhatsAppFab";
import DeliveryEstimate from "@/components/DeliveryEstimate";
import { api, resolveImageUri, formatINR } from "@/lib/api";
import { getPriceInfo, discountPercent, maxQtyFor, isComboOffer, comboStep, comboMinQty, freeUnits } from "@/lib/pricing";
import { useStore } from "@/context/StoreContext";
import { toast } from "sonner";

function Stars({ value = 0, size = 14 }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={size} className={n <= Math.round(value) ? "fill-warn text-warn" : "text-line"} />
      ))}
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { addItem, verified } = useStore();
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);

  const { data: product, isLoading } = useQuery({ queryKey: ["product", id], queryFn: () => api.getProduct(id) });
  const { data: reviewData } = useQuery({ queryKey: ["reviews", id], queryFn: () => api.getReviews(id) });

  const [rvName, setRvName] = useState("");
  const [rvRating, setRvRating] = useState(5);
  const [rvComment, setRvComment] = useState("");
  const [rvPhone, setRvPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (product) setQty(comboMinQty(product));
  }, [product?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading || !product) {
    return (
      <div className="min-h-screen bg-paper">
        <Header />
        <div className="py-24 text-center text-muted2">Loading…</div>
      </div>
    );
  }

  const info = getPriceInfo(product, verified);
  const disc = discountPercent(info);
  const gallery = (product.images && product.images.length ? product.images : [product.image]).filter(Boolean);
  const cap = maxQtyFor(product);
  const offer = isComboOffer(product);
  const step = comboStep(product);
  const minQ = comboMinQty(product);

  const add = () => {
    addItem(product, qty);
    toast.success(`${product.name} × ${qty} added to cart`);
  };

  const submitReview = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.addReview(id, { name: rvName, rating: rvRating, comment: rvComment, altos_verified: verified, phone: rvPhone });
      toast.success("Thanks! Your review is submitted and will appear once approved.");
      setRvName(""); setRvComment(""); setRvPhone(""); setRvRating(5);
      qc.invalidateQueries({ queryKey: ["reviews", id] });
      qc.invalidateQueries({ queryKey: ["product", id] });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper">
      <Header />
      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted2 hover:text-brand mb-5" data-testid="back-button">
          <ChevronLeft size={16} /> Back
        </button>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {/* Gallery */}
          <div>
            <div className="w-full aspect-square bg-mist rounded-2xl overflow-hidden">
              <img src={resolveImageUri(gallery[activeImg])} alt={product.name} className="w-full h-full object-cover" data-testid="product-main-image" />
            </div>
            {gallery.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto hide-scrollbar">
                {gallery.map((g, i) => (
                  <button key={`${g}-${i}`} onClick={() => setActiveImg(i)} className={`shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${i === activeImg ? "border-brand" : "border-transparent"}`}>
                    <img src={resolveImageUri(g)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <p className="text-[11px] uppercase tracking-wider text-muted2 font-semibold">{product.category}{product.weight ? ` · ${product.weight}` : ""}</p>
            <h1 className="font-display text-4xl sm:text-5xl text-ink leading-tight mt-1" data-testid="product-name">{product.name}</h1>

            {(product.rating_count || 0) > 0 && (
              <div className="flex items-center gap-2 mt-3">
                <Stars value={product.rating_avg} />
                <span className="text-sm text-muted2">{product.rating_avg} · {product.rating_count} reviews</span>
              </div>
            )}

            <div className="flex items-baseline gap-3 mt-4">
              <span className="text-3xl font-semibold text-ink" data-testid="product-price">{formatINR(info.unit)}</span>
              {info.compareAt !== null && <span className="text-lg text-muted2 line-through">{formatINR(info.compareAt)}</span>}
              {disc > 0 && <span className="text-sm font-semibold text-ok">{disc}% OFF</span>}
            </div>
            {verified && Number(product.bv) > 0 && <p className="text-sm font-semibold text-brand mt-1">BV {Number(product.bv)} points</p>}
            {!verified && <p className="text-xs text-muted2 mt-1">Altos ID holders get special DP pricing — log in to view.</p>}

            {offer && (
              <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-crimson/10 text-crimson font-semibold text-sm px-4 py-1.5" data-testid="combo-offer-badge">
                <Sparkles size={15} /> Buy 3 Get 1 Free
              </div>
            )}

            <p className="text-[15px] text-ink/80 leading-relaxed mt-5">{product.description}</p>

            {product.dosage && (
              <div className="mt-5 rounded-xl border border-line bg-mist p-4" data-testid="dosage-box">
                <p className="text-xs uppercase tracking-wider text-brand font-semibold mb-1">Dosage</p>
                <p className="text-sm text-ink/80">{product.dosage}</p>
              </div>
            )}

            {/* Qty + Add */}
            <div className="flex items-center gap-4 mt-6">
              <div className="flex items-center border border-line rounded-full">
                <button onClick={() => setQty((q) => Math.max(minQ, q - step))} className="w-11 h-11 flex items-center justify-center text-ink" data-testid="qty-minus"><Minus size={16} /></button>
                <span className="w-8 text-center font-semibold" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty((q) => Math.min(cap, q + step))} className="w-11 h-11 flex items-center justify-center text-ink" data-testid="qty-plus"><Plus size={16} /></button>
              </div>
              <button onClick={add} data-testid="add-to-cart-button" className="flex-1 h-12 rounded-full bg-brand text-white font-semibold flex items-center justify-center gap-2 hover:bg-brand/90 transition-colors">
                <ShoppingBag size={18} /> Add to cart
              </button>
            </div>
            {offer ? (
              <p className="text-xs text-crimson font-semibold mt-2" data-testid="combo-free-note">
                {qty >= 3 ? "Buy 3 Get 1 Free — you'll get 1 unit free!" : `Buy 3 to get 1 free — add ${3 - qty} more (max 3).`}
              </p>
            ) : cap < 99 ? (
              <p className="text-xs text-warn mt-2">Max {cap} pcs of this heavy item per order.</p>
            ) : null}

            {/* Shipping info */}
            <div className="mt-6 rounded-xl border border-line p-4 flex gap-3">
              <Truck className="text-brand shrink-0" size={20} />
              <div className="text-sm text-ink/80">
                <p>Dispatched within 2 days of your order.</p>
                <p className="text-xs text-muted2 mt-0.5">Shipping is calculated at checkout based on order weight & value. Enter your PIN below for a delivery estimate.</p>
              </div>
            </div>

            <DeliveryEstimate />

            {product.category === "Supplements" && (
              <div className="mt-4 rounded-xl border border-warn/30 bg-warn/5 p-4 flex gap-3" data-testid="ayurvedic-disclaimer">
                <ShieldAlert className="text-warn shrink-0" size={18} />
                <p className="text-xs text-ink/70 leading-relaxed">These statements are for general wellness and are not intended to diagnose, treat, cure or prevent any disease. Consult your healthcare provider if pregnant, nursing, on medication or managing a condition.</p>
              </div>
            )}
          </div>
        </div>

        {/* Ingredients / Benefits */}
        {(product.ingredients || product.benefits) && (
          <div className="grid sm:grid-cols-2 gap-5 mt-12">
            {product.ingredients && (
              <div className="rounded-2xl border border-line p-6" data-testid="ingredients-box">
                <h3 className="font-display text-2xl text-ink flex items-center gap-2 mb-2"><Leaf className="text-brand" size={20} /> Ingredients</h3>
                <p className="text-sm text-ink/80 leading-relaxed whitespace-pre-line">{product.ingredients}</p>
              </div>
            )}
            {product.benefits && (
              <div className="rounded-2xl border border-line p-6" data-testid="benefits-box">
                <h3 className="font-display text-2xl text-ink flex items-center gap-2 mb-2"><Heart className="text-brand" size={20} /> Benefits</h3>
                <p className="text-sm text-ink/80 leading-relaxed whitespace-pre-line">{product.benefits}</p>
              </div>
            )}
          </div>
        )}

        {/* Reviews */}
        <section className="mt-14">
          <h2 className="font-display text-3xl text-ink mb-5">Customer Reviews</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              {(reviewData?.reviews || []).length === 0 && <p className="text-sm text-muted2">No reviews yet. Be the first to review!</p>}
              {(reviewData?.reviews || []).map((r) => (
                <div key={r.id} className="rounded-xl border border-line p-4" data-testid={`review-${r.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-ink text-sm">{r.name}</span>
                    <Stars value={r.rating} size={12} />
                  </div>
                  {(r.verified_buyer || r.altos_holder) && (
                    <span className="inline-block text-[10px] font-semibold text-ok bg-brandter rounded-full px-2 py-0.5 mt-1">{r.altos_holder ? "Altos ID holder" : "Verified buyer"}</span>
                  )}
                  {r.comment && <p className="text-sm text-ink/70 mt-2">{r.comment}</p>}
                </div>
              ))}
            </div>

            <form onSubmit={submitReview} className="rounded-2xl border border-line p-6 h-fit" data-testid="review-form">
              <h3 className="font-display text-2xl text-ink mb-3">Write a review</h3>
              <div className="flex items-center gap-2 mb-3">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button type="button" key={n} onClick={() => setRvRating(n)}>
                    <Star size={22} className={n <= rvRating ? "fill-warn text-warn" : "text-line"} />
                  </button>
                ))}
              </div>
              <input value={rvName} onChange={(e) => setRvName(e.target.value)} placeholder="Your name" required className="w-full h-11 rounded-lg border border-line px-3 text-sm mb-3 focus:outline-none focus:border-brand" data-testid="review-name" />
              {!verified && (
                <input value={rvPhone} onChange={(e) => setRvPhone(e.target.value)} placeholder="Mobile number used at purchase" className="w-full h-11 rounded-lg border border-line px-3 text-sm mb-3 focus:outline-none focus:border-brand" data-testid="review-phone" />
              )}
              <textarea value={rvComment} onChange={(e) => setRvComment(e.target.value)} placeholder="Share your experience…" rows={3} className="w-full rounded-lg border border-line px-3 py-2 text-sm mb-3 focus:outline-none focus:border-brand" data-testid="review-comment" />
              <button disabled={submitting} type="submit" className="w-full h-11 rounded-lg bg-brand text-white font-semibold disabled:opacity-60" data-testid="review-submit">
                {submitting ? "Submitting…" : "Submit review"}
              </button>
              <p className="text-[11px] text-muted2 mt-2">Only Altos ID holders or verified buyers can post reviews.</p>
            </form>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFab />
    </div>
  );
}
